/**
 * User Service - User Profile and Management
 *
 * Handles:
 * - Profile retrieval and updates
 * - Password changes
 * - User preferences
 *
 * Backend Integration: app/user/* endpoints
 */

import apiService from './apiClient';
import logger from '../utils/logger';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  mobile: string;
  username?: string;
  profileImageUrl?: string;
  designation?: string;
  department?: string;
  employeeCode?: string;
  isActive: boolean;
  userRoleName?: string;
  reportingManagerId?: string;
  reportingManagerName?: string;
  teamName?: string;
  profile?: {
    dateOfBirth?: string;
    dateOfJoining?: string;
    gender?: string;
    address?: string;
    emergencyContactName?: string;
    emergencyContactNumber?: string;
  };
  customFields?: Record<string, any>;
  profileCompletionPercentage?: number;
  preferences?: {
    language?: string;
    theme?: string;
    notificationsEnabled?: boolean;
  };
}

export interface UpdateProfileDto {
  username?: string;
  name?: string;
  email?: string;
  mobile?: string;
  profileImageUrl?: string;
  address?: string;
  preferences?: Record<string, any>;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ApiResponse<T> {
  success?: boolean;
  statusCode: number;
  message?: string;
  data: T;
}

// ============================================================================
// USER SERVICE
// ============================================================================

class UserService {
  private readonly BASE_PATH = '/web/user';

  /**
   * Get current user's profile
   */
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    try {
      const response = await apiService.get<ApiResponse<UserProfile>>(
        `${this.BASE_PATH}/profile`
      );

      logger.info('Profile retrieved successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to get profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileDto): Promise<ApiResponse<UserProfile>> {
    try {
      const response = await apiService.put<ApiResponse<UserProfile>>(
        `${this.BASE_PATH}/profile`,
        data
      );

      logger.info('Profile updated successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to update profile:', error);
      throw error;
    }
  }

  /**
   * Upload profile image
   * Uses dedicated uploadFile method for proper FormData handling
   */
  async uploadProfileImage(file: File): Promise<ApiResponse<{ profilePic: string }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use uploadFile for proper FormData handling (no manual Content-Type)
      const response = await apiService.uploadFile<ApiResponse<{ profilePic: string }>>(
        `${this.BASE_PATH}/profile/image`,
        formData
      );

      logger.info('Profile image uploaded successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to upload profile image:', error);
      throw error;
    }
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordDto): Promise<ApiResponse<{ message: string }>> {
    try {
      // Validate passwords match
      if (data.newPassword !== data.confirmPassword) {
        throw new Error('New password and confirm password do not match');
      }

      const response = await apiService.post<ApiResponse<{ message: string }>>(
        `${this.BASE_PATH}/change-password`,
        {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        }
      );

      logger.info('Password changed successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to change password:', error);
      throw error;
    }
  }

  /**
   * Get user preferences
   */
  async getPreferences(): Promise<ApiResponse<Record<string, any>>> {
    try {
      const response = await apiService.get<ApiResponse<Record<string, any>>>(
        `${this.BASE_PATH}/preferences`
      );

      logger.info('Preferences retrieved successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to get preferences:', error);
      throw error;
    }
  }

  /**
   * Update user preferences
   */
  async updatePreferences(preferences: Record<string, any>): Promise<ApiResponse<Record<string, any>>> {
    try {
      const response = await apiService.put<ApiResponse<Record<string, any>>>(
        `${this.BASE_PATH}/preferences`,
        preferences
      );

      logger.info('Preferences updated successfully');
      return response;
    } catch (error: any) {
      logger.error('Failed to update preferences:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const userService = new UserService();
export default userService;
